import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import Reveal from "../../components/Reveal";

const FEATURES = [
  "Real-time resume preview as the user fills in sections",
  "ATS-optimised output format for maximum recruiter reach",
  "Structured section templates (experience, education, skills)",
  "Clean, professional design with multiple layout options",
  "Export / print-ready output",
  "Responsive design: works on desktop and mobile",
];

export default function ProjectResumeArchitecture() {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pt-32 md:px-12 md:pt-40">
        <Link to="/projects" className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint hover:text-heading">
          &larr; Back to Projects
        </Link>
      </div>

      <Reveal className="mx-auto max-w-content px-6 pb-14 pt-8 md:px-12 md:pb-20">
        <p className="eyebrow">Web Development &middot; Career Tools</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold leading-tight text-heading md:text-5xl">
          Resume <em className="italic text-gold-500">Architecture</em>
        </h1>
        <p className="mt-6 max-w-[62ch] text-[1rem] leading-relaxed text-ink-soft">
          A web platform for crafting professionally structured, ATS-optimised resumes with real-time
          preview, helping job seekers present their experience clearly to recruiters and automated
          screening systems.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <a
            href="https://resumearchitecture.netlify.app"
            target="_blank"
            rel="noreferrer"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            View Live Site
          </a>
          <a
            href="https://github.com/ellenboakye/resumearchitect"
            target="_blank"
            rel="noreferrer"
            className="text-[0.75rem] uppercase tracking-widest2 text-ink-soft hover:text-heading"
          >
            GitHub Repo
          </a>
        </div>
      </Reveal>

      <section className="border-y border-line bg-surface-alt">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12">
          <div className="flex aspect-video items-center justify-center border border-line bg-surface">
            <div className="text-center">
              <p className="eyebrow">Live Preview</p>
              <p className="mt-2 font-serif text-lg text-heading">resumearchitecture.netlify.app</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-line">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-20">
          <p className="eyebrow">Project Walkthrough</p>
          <h2 className="mt-2 font-serif text-2xl text-heading">See it in action</h2>
          <div className="relative mt-8 aspect-video border border-line bg-surface-alt">
            <video
              ref={videoRef}
              controls
              src="/projects/resume-architecture.mp4"
              className="h-full w-full object-cover"
              style={{ display: videoReady ? "block" : "none" }}
              onLoadedData={() => setVideoReady(true)}
            />
            {!videoReady && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-gold-300 text-lg text-gold-500">▶</div>
                <p className="font-serif text-lg text-ink-soft">Resume Architecture: Walkthrough</p>
                <p className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Place resume-architecture.mp4 in public/projects/ to preview</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
        <div className="grid gap-14 md:grid-cols-[1fr_320px] md:gap-16">
          <div>
            <Reveal className="mb-16">
              <p className="eyebrow">Overview</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">What it does</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Resume Architecture is a web application that guides users through building a structured,
                professional resume. The platform focuses on clarity, readability, and ATS (Applicant
                Tracking System) compatibility, ensuring resumes are both human-friendly and
                machine-readable.
              </p>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Update this section with more detail about the project background, the problem it solves,
                and any key decisions made during development.
              </p>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Key Features</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">What was built</h2>
              <ul className="mt-6 divide-y divide-line border-t border-line">
                {FEATURES.map((f) => (
                  <li key={f} className="flex gap-3 py-3.5 text-[0.9rem] leading-relaxed text-ink-soft">
                    <span className="mt-[0.6em] h-1 w-1 flex-shrink-0 rounded-full bg-gold-500" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[0.75rem] uppercase tracking-widest2 text-gold-500/70">
                ○ Update features list with the actual functionality built.
              </p>
            </Reveal>

            <Reveal>
              <p className="eyebrow">Development</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">Process &amp; challenges</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Add notes here about how the build was approached, architecture decisions, interesting
                problems solved, and what was learned during development. This is the section that shows
                technical depth to anyone reading the project.
              </p>
            </Reveal>
          </div>

          <Reveal as="aside" className="h-fit space-y-6 md:sticky md:top-28">
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Tech Stack</p>
              <p className="text-[0.85rem] leading-relaxed text-ink-soft">HTML &middot; CSS &middot; JavaScript</p>
              <p className="mt-3 text-[0.75rem] uppercase tracking-widest2 text-gold-500/70">○ Update with the actual stack</p>
            </div>
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Project Info</p>
              <dl className="space-y-3 text-[0.85rem]">
                <div><dt className="text-ink-faint">Type</dt><dd className="text-heading">Web Application</dd></div>
                <div><dt className="text-ink-faint">Status</dt><dd className="text-heading">Live</dd></div>
                <div><dt className="text-ink-faint">Year</dt><dd className="text-heading">2024 – 2025</dd></div>
              </dl>
            </div>
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Links</p>
              <a href="https://resumearchitecture.netlify.app" target="_blank" rel="noreferrer" className="block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading">
                resumearchitecture.netlify.app &rarr;
              </a>
              <a href="https://github.com/ellenboakye/resumearchitect" target="_blank" rel="noreferrer" className="mt-3 block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading">
                View on GitHub &rarr;
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <section className="border-t border-line">
        <div className="mx-auto flex max-w-content flex-wrap items-center justify-between gap-6 px-6 py-14 md:px-12">
          <p className="font-serif text-xl text-heading">See all projects</p>
          <Link
            to="/projects"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            Browse Projects &rarr;
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
